import React, { useState, useEffect } from 'react';
import { X, Save, Upload, FileText, Trash2, Package, Eye } from 'lucide-react';
import { useData } from '../contexts/DataContext';
import { Tender, TenderItem } from '../types';

interface TenderFormProps {
  tender?: Tender | null;
  onClose: () => void;
}

const TenderForm: React.FC<TenderFormProps> = ({ tender, onClose }) => {
  const { 
    organizations, 
    tenderTypes, 
    tenderStatuses, 
    applicationStatuses,
    applications,
    addTender, 
    updateTender 
  } = useData();

  const [formData, setFormData] = useState({
    tender_code: '',
    title: '',
    description: '',
    organization_id: '',
    type_id: '',
    status_id: '',
    application_status_id: '',
    amount: '',
    currency: 'CLP' as 'CLP' | 'USD' | 'EUR',
    contract_months: '',
    publish_date: '',
    closing_date: '',
    award_date: '',
    requirements: '',
    has_technical_visit: false,
    technical_visit_date: '',
    has_gso: false,
    gso_amount: '',
    gso_delivery_date: '',
    has_gfc: false,
    gfc_amount: '',
    gfc_delivery_date: '',
    fecha_preguntas: '',
    items_count: ''
  });

  const [documents, setDocuments] = useState<Array<{title: string, file: File | null}>>([]);
  const [items, setItems] = useState<TenderItem[]>([]);
  const [showItemsGrid, setShowItemsGrid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Check if tender has applications
  const hasBids = tender ? applications.some(app => app.tender_id === tender.id) : false;

  // Obtener el estado "Pendiente" por defecto
  const getPendingApplicationStatus = () => {
    return applicationStatuses.find(status => 
      status.name === 'Pendiente' || 
      status.order_index === 1
    ) || applicationStatuses[0];
  };

  // Obtener el estado "Publicada" por defecto para licitaciones
  const getPublishedTenderStatus = () => {
    return tenderStatuses.find(status => status.name === 'Publicada');
  };

  useEffect(() => {
    if (tender) {
      setFormData({
        tender_code: tender.tender_code,
        title: tender.title,
        description: tender.description,
        organization_id: tender.organization_id,
        type_id: tender.type_id,
        status_id: tender.status_id,
        application_status_id: tender.application_status_id || '',
        amount: tender.amount.toString(),
        currency: tender.currency,
        contract_months: tender.contract_months?.toString() || '',
        publish_date: tender.publish_date?.split('T')[0] || '',
        closing_date: tender.closing_date?.split('T')[0] || '',
        award_date: tender.award_date?.split('T')[0] || '',
        requirements: tender.requirements || '',
        has_technical_visit: tender.has_technical_visit || false,
        technical_visit_date: tender.technical_visit_date?.split('T')[0] || '',
        has_gso: tender.has_gso || false,
        gso_amount: tender.gso_amount?.toString() || '',
        gso_delivery_date: (tender as any).gso_delivery_date?.split('T')[0] || '',
        has_gfc: tender.has_gfc || false,
        gfc_amount: tender.gfc_amount?.toString() || '',
        gfc_delivery_date: (tender as any).gfc_delivery_date?.split('T')[0] || '',
        fecha_preguntas: tender.fecha_preguntas?.split('T')[0] || '',
        items_count: tender.items_count?.toString() || ''
      });
      
      // Cargar documentos existentes
      if (tender.documents && tender.documents.length > 0) {
        const loadedDocs = tender.documents.map(doc => ({
          title: doc.split('.')[0], // Remove extension for title
          file: null // We don't have the actual file object for existing docs
        }));
        setDocuments(loadedDocs);
      }
      
      // Cargar items existentes
      if (tender.items && tender.items.length > 0) {
        setItems(tender.items);
        setShowItemsGrid(true);
      }
    } else {
      // Establecer valores por defecto para nueva licitación
      const defaultTenderStatus = getPublishedTenderStatus();
      const defaultApplicationStatus = getPendingApplicationStatus();
      
      setFormData(prev => ({
        ...prev,
        status_id: defaultTenderStatus?.id || '',
        application_status_id: defaultApplicationStatus?.id || ''
      }));
    }
  }, [tender, tenderStatuses, applicationStatuses]);

  // Calcular automáticamente el items_count cuando cambian los items
  useEffect(() => {
    const totalCount = items.reduce((sum, item) => sum + (item.Cantidad || 0), 0);
    setFormData(prev => ({ ...prev, items_count: totalCount.toString() }));
  }, [items]);

  // Auto-marcar "Tiene Visita Técnica" si hay una fecha válida
  useEffect(() => {
    if (formData.technical_visit_date) {
      const isValidDate = !isNaN(new Date(formData.technical_visit_date).getTime());
      if (isValidDate && !formData.has_technical_visit) {
        setFormData(prev => ({ ...prev, has_technical_visit: true }));
      }
    }
  }, [formData.technical_visit_date]);

  // Helper function to determine if a field should be read-only
  const isReadOnly = (fieldName: string) => {
    if (!tender) return false; // New tender, all fields editable
    
    const readOnlyFields = [
      'tender_code',
      'title', 
      'description',
      'organization_id',
      'type_id',
      'status_id',
      'items_count',
      'publish_date',
      'closing_date',
      'award_date',
      'fecha_preguntas',
      'technical_visit_date',
      'contract_months', // Agregar meses de contrato como solo lectura
      'has_technical_visit' // Agregar checkbox como solo lectura
    ];
    
    return readOnlyFields.includes(fieldName);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if tender has bids
    if (tender && hasBids) {
      return;
    }
    
    setIsLoading(true);

    try {
      // Process document files and extract file names
      const documentNames = documents.map(doc => {
        // Use the title as the file name if no file is selected
        if (!doc.file) return doc.title;
        
        // Otherwise use the file name
        const fileName = doc.file.name;
        return fileName;
      });

      const tenderData = {
        tender_code: formData.tender_code,
        title: formData.title,
        description: formData.description,
        organization_id: formData.organization_id,
        type_id: formData.type_id,
        status_id: formData.status_id,
        application_status_id: formData.application_status_id || undefined,
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        contract_months: formData.contract_months ? parseInt(formData.contract_months) : undefined,
        publish_date: new Date(formData.publish_date).toISOString(),
        closing_date: new Date(formData.closing_date).toISOString(),
        award_date: formData.award_date ? new Date(formData.award_date).toISOString() : undefined,
        requirements: formData.requirements,
        has_technical_visit: formData.has_technical_visit,
        technical_visit_date: formData.technical_visit_date ? new Date(formData.technical_visit_date).toISOString() : undefined,
        has_gso: formData.has_gso,
        gso_amount: formData.gso_amount ? parseFloat(formData.gso_amount) : undefined,
        gso_delivery_date: formData.gso_delivery_date ? new Date(formData.gso_delivery_date).toISOString() : undefined,
        has_gfc: formData.has_gfc,
        gfc_amount: formData.gfc_amount ? parseFloat(formData.gfc_amount) : undefined,
        gfc_delivery_date: formData.gfc_delivery_date ? new Date(formData.gfc_delivery_date).toISOString() : undefined,
        fecha_preguntas: formData.fecha_preguntas ? new Date(formData.fecha_preguntas).toISOString() : undefined,
        items_count: parseInt(formData.items_count) || 0,
        items: items,
        documents: documentNames
      };

      if (tender) {
        updateTender(tender.id, tenderData);
      } else {
        addTender(tenderData);
      }

      onClose();
    } catch (error) {
      console.error('Error saving tender:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (name: string, checked: boolean) => {
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const addDocument = () => {
    setDocuments(prev => [...prev, { title: '', file: null }]);
  };

  const updateDocument = (index: number, field: 'title' | 'file', value: string | File) => {
    setDocuments(prev => prev.map((doc, i) => 
      i === index ? { ...doc, [field]: value } : doc
    ));
    
    // If updating file, also update the title to the file name without extension
    if (field === 'file' && value instanceof File) {
      const fileName = value.name.split('.')[0]; // Remove extension
      setDocuments(prev => prev.map((doc, i) => 
        i === index ? { ...doc, title: fileName } : doc
      ));
    }
  };

  const removeDocument = (index: number) => {
    setDocuments(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {tender ? 'Editar Licitación' : 'Nueva Licitación'}
          </h1>
          <p className="text-gray-600">
            {tender ? 'Modifica los datos de la licitación (algunos campos son de solo lectura)' : 'Completa la información de la nueva licitación'}
          </p>
        </div>
        <button
          onClick={onClose}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <X className="h-6 w-6" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* ID Licitación - PRIMER CAMPO */}
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ID Licitación {tender && <span className="text-xs text-gray-500">(Solo lectura)</span>}
            </label>
            <input
              type="text"
              name="tender_code"
              value={formData.tender_code}
              onChange={handleChange}
              readOnly={isReadOnly('tender_code')}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                isReadOnly('tender_code') ? 'bg-gray-50 text-gray-700 cursor-not-allowed' : ''
              }`}
              placeholder="Ej: ABC-0001 (se generará automáticamente si se deja vacío)"
            />
            <p className="text-xs text-gray-500 mt-1">
              {tender 
                ? 'El ID de licitación no se puede modificar'
                : 'Se generará automáticamente basado en el nombre de la empresa si se deja vacío'
              }
            </p>
          </div>

          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Título * {tender && <span className="text-xs text-gray-500">(Solo lectura)</span>}
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              readOnly={isReadOnly('title')}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                isReadOnly('title') ? 'bg-gray-50 text-gray-700 cursor-not-allowed' : ''
              }`}
              placeholder="Título de la licitación"
            />
          </div>

          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descripción * {tender && <span className="text-xs text-gray-500">(Solo lectura)</span>}
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              readOnly={isReadOnly('description')}
              rows={4}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                isReadOnly('description') ? 'bg-gray-50 text-gray-700 cursor-not-allowed' : ''
              }`}
              placeholder="Descripción detallada de la licitación"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Organización * {tender && <span className="text-xs text-gray-500">(Solo lectura)</span>}
            </label>
            <select
              name="organization_id"
              value={formData.organization_id}
              onChange={handleChange}
              required
              disabled={isReadOnly('organization_id')}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                isReadOnly('organization_id') ? 'bg-gray-50 text-gray-700 cursor-not-allowed' : ''
              }`}
            >
              <option value="">Seleccionar organización</option>
              {organizations.map(org => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Licitación * {tender && <span className="text-xs text-gray-500">(Solo lectura)</span>}
            </label>
            <select
              name="type_id"
              value={formData.type_id}
              onChange={handleChange}
              required
              disabled={isReadOnly('type_id')}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                isReadOnly('type_id') ? 'bg-gray-50 text-gray-700 cursor-not-allowed' : ''
              }`}
            >
              <option value="">Seleccionar tipo</option>
              {tenderTypes.map(type => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Estado * {tender && <span className="text-xs text-gray-500">(Solo lectura)</span>}
            </label>
            <select
              name="status_id"
              value={formData.status_id}
              onChange={handleChange}
              required
              disabled={isReadOnly('status_id')}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                isReadOnly('status_id') ? 'bg-gray-50 text-gray-700 cursor-not-allowed' : ''
              }`}
            >
              <option value="">Seleccionar estado</option>
              {tenderStatuses.map(status => (
                <option key={status.id} value={status.id}>
                  {status.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Monto *
            </label>
            <div className="flex">
              <select
                name="currency"
                value={formData.currency}
                onChange={handleChange}
                className="px-3 py-2 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
              >
                <option value="CLP">CLP</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </select>
              <input
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                required
                min="0"
                step="0.01"
                className="flex-1 px-3 py-2 border border-l-0 border-gray-300 rounded-r-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0.00"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Meses de Contrato {tender && <span className="text-xs text-gray-500">(Solo lectura)</span>}
            </label>
            <input
              type="number"
              name="contract_months"
              value={formData.contract_months}
              onChange={handleChange}
              min="1"
              readOnly={isReadOnly('contract_months')}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                isReadOnly('contract_months') ? 'bg-gray-50 text-gray-700 cursor-not-allowed' : ''
              }`}
              placeholder="12"
            />
            {tender && (
              <p className="text-xs text-gray-500 mt-1">
                Valor importado desde Mercado Público (no editable)
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Estado de Postulación (Solo lectura)
            </label>
            <select
              name="application_status_id"
              value={formData.application_status_id}
              onChange={handleChange}
              disabled={true}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed"
            >
              <option value="">Sin estado de postulación</option>
              {applicationStatuses.sort((a, b) => a.order_index - b.order_index).map(status => (
                <option key={status.id} value={status.id}>
                  {status.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Este campo se actualiza automáticamente cuando cambias el estado de las postulaciones relacionadas
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fecha de Publicación * {tender && <span className="text-xs text-gray-500">(Solo lectura)</span>}
            </label>
            <input
              type="date"
              name="publish_date"
              value={formData.publish_date}
              onChange={handleChange}
              required
              readOnly={isReadOnly('publish_date')}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                isReadOnly('publish_date') ? 'bg-gray-50 text-gray-700 cursor-not-allowed' : ''
              }`}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fecha de Cierre * {tender && <span className="text-xs text-gray-500">(Solo lectura)</span>}
            </label>
            <input
              type="date"
              name="closing_date"
              value={formData.closing_date}
              onChange={handleChange}
              required
              readOnly={isReadOnly('closing_date')}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                isReadOnly('closing_date') ? 'bg-gray-50 text-gray-700 cursor-not-allowed' : ''
              }`}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fecha de Adjudicación {tender && <span className="text-xs text-gray-500">(Solo lectura)</span>}
            </label>
            <input
              type="date"
              name="award_date"
              value={formData.award_date}
              onChange={handleChange}
              readOnly={isReadOnly('award_date')}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                isReadOnly('award_date') ? 'bg-gray-50 text-gray-700 cursor-not-allowed' : ''
              }`}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fecha Límite para Preguntas {tender && <span className="text-xs text-gray-500">(Solo lectura)</span>}
            </label>
            <input
              type="date"
              name="fecha_preguntas"
              value={formData.fecha_preguntas}
              onChange={handleChange}
              readOnly={isReadOnly('fecha_preguntas')}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                isReadOnly('fecha_preguntas') ? 'bg-gray-50 text-gray-700 cursor-not-allowed' : ''
              }`}
            />
            <p className="text-xs text-gray-500 mt-1">
              {tender 
                ? 'Fecha importada desde Mercado Público (no editable)'
                : 'Fecha límite para realizar consultas sobre la licitación'
              }
            </p>
          </div>

          {/* Visita Técnica - Checkbox ANTES de la fecha */}
          <div className="lg:col-span-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Checkbox primero */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="has_technical_visit"
                  checked={formData.has_technical_visit}
                  onChange={(e) => handleCheckboxChange('has_technical_visit', e.target.checked)}
                  disabled={isReadOnly('has_technical_visit')}
                  className={`h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded ${
                    isReadOnly('has_technical_visit') ? 'cursor-not-allowed opacity-50' : ''
                  }`}
                />
                <label htmlFor="has_technical_visit" className="ml-2 text-sm font-medium text-gray-700">
                  Tiene Visita Técnica {tender && <span className="text-xs text-gray-500">(Solo lectura)</span>}
                </label>
              </div>
              
              {/* Fecha después */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha Visita Técnica {tender && <span className="text-xs text-gray-500">(Solo lectura)</span>}
                </label>
                <input
                  type="date"
                  name="technical_visit_date"
                  value={formData.technical_visit_date}
                  onChange={handleChange}
                  readOnly={isReadOnly('technical_visit_date')}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isReadOnly('technical_visit_date') ? 'bg-gray-50 text-gray-700 cursor-not-allowed' : ''
                  }`}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {tender 
                    ? 'Fecha importada desde Mercado Público (no editable)'
                    : 'Fecha programada para la visita técnica'
                  }
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Requisitos
            </label>
            <textarea
              name="requirements"
              value={formData.requirements}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Requisitos específicos para la licitación"
            />
          </div>

          {/* Sección de Items - Solo lectura */}
          <div className="lg:col-span-2">
            <div className="flex justify-between items-center mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Items de la Licitación
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  {items.length > 0 
                    ? `${items.length} item${items.length !== 1 ? 's' : ''} importados desde Mercado Público (solo lectura)`
                    : 'No hay items importados desde Mercado Público'
                  }
                </p>
              </div>
              {items.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowItemsGrid(!showItemsGrid)}
                  className="bg-gray-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-gray-700 transition-colors flex items-center"
                >
                  <Eye className="h-4 w-4 mr-1" />
                  {showItemsGrid ? 'Ocultar' : 'Ver'} Items
                </button>
              )}
            </div>

            {items.length > 0 && showItemsGrid && (
              <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-100 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Item
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Unidad
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Cantidad
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Proveedor
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Monto Unit.
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {items.map((item, index) => (
                        <tr key={item.id || index} className="hover:bg-gray-50">
                          <td className="px-4 py-4">
                            <div className="text-sm font-medium text-gray-900">
                              {item.NombreProducto}
                            </div>
                            {item.CodigoProducto && (
                              <div className="text-xs text-gray-500 font-mono">
                                {item.CodigoProducto}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <div className="text-sm text-gray-900">
                              {item.UnidadMedida || '-'}
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="text-sm text-gray-900 font-medium">
                              {(item.Cantidad || 0).toLocaleString()}
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            {item.RutProveedor && item.NombreProveedor ? (
                              <div className="space-y-1">
                                <div className="text-xs text-green-600 font-medium">Adjudicado</div>
                                <div className="text-xs text-gray-600">
                                  <div className="font-medium">{item.NombreProveedor}</div>
                                  <div className="font-mono">{item.RutProveedor}</div>
                                </div>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">Sin adjudicar</span>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            {item.MontoUnitario ? (
                              <div className="text-sm font-medium text-green-600">
                                {formData.currency} {item.MontoUnitario.toLocaleString()}
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {items.length === 0 && (
              <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
                <Package className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                <p className="text-sm">No hay items importados</p>
                <p className="text-xs">Los items se importan automáticamente desde Mercado Público</p>
              </div>
            )}
          </div>

          {/* GSO */}
          <div>
            <div className="flex items-center mb-4">
              <input
                type="checkbox"
                id="has_gso"
                checked={formData.has_gso}
                onChange={(e) => handleCheckboxChange('has_gso', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="has_gso" className="ml-2 text-sm font-medium text-gray-700">
                GSO (Garantía Seriedad de Oferta)
              </label>
            </div>
            {formData.has_gso && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Monto GSO
                  </label>
                  <input
                    type="number"
                    name="gso_amount"
                    value={formData.gso_amount}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fecha de Entrega GSO
                  </label>
                  <input
                    type="date"
                    name="gso_delivery_date"
                    value={formData.gso_delivery_date}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}
          </div>

          {/* GFC */}
          <div>
            <div className="flex items-center mb-4">
              <input
                type="checkbox"
                id="has_gfc"
                checked={formData.has_gfc}
                onChange={(e) => handleCheckboxChange('has_gfc', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="has_gfc" className="ml-2 text-sm font-medium text-gray-700">
                GFC (Garantía Fiel Cumplimiento)
              </label>
            </div>
            {formData.has_gfc && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Monto GFC
                  </label>
                  <input
                    type="number"
                    name="gfc_amount"
                    value={formData.gfc_amount}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fecha de Entrega GFC
                  </label>
                  <input
                    type="date"
                    name="gfc_delivery_date"
                    value={formData.gfc_delivery_date}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Documentos */}
          <div className="lg:col-span-2">
            <div className="flex justify-between items-center mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Documentos
              </label>
              <button
                type="button"
                onClick={addDocument}
                className="bg-green-600 text-white px-3 py-1 rounded-lg text-sm hover:bg-green-700 transition-colors flex items-center"
              >
                <Upload className="h-4 w-4 mr-1" />
                Agregar Documento
              </button>
            </div>
            
            <div className="space-y-3">
              {documents.map((doc, index) => (
                <div key={index} className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg">
                  <FileText className="h-5 w-5 text-gray-400" />
                  <div className="flex-1">
                    <input
                      type="text"
                      placeholder="Título del documento"
                      value={doc.title}
                      onChange={(e) => updateDocument(index, 'title', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div className="flex-1">
                    <input
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          updateDocument(index, 'file', file);
                        }
                      }}
                      className="w-full text-sm text-gray-500 file:mr-4 file:py-1 file:px-2 file:rounded file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeDocument(index)}
                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
              
              {documents.length === 0 && (
                <div className="text-center py-6 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
                  <Upload className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm">No hay documentos agregados</p>
                  <p className="text-xs">Haz clic en "Agregar Documento" para subir archivos</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isLoading || hasBids}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
          >
            <Save className="h-4 w-4 mr-2" />
            {isLoading ? 'Guardando...' : tender ? 'Actualizar' : 'Crear Licitación'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TenderForm;